import { gql } from '@apollo/client'
import { useTranslations } from '@npco/common-utils-translations'
import { TERMS_CONDITIONS_URL } from '@npco/component-mp-common'
import { Box, COLOR, Flex, Tutorial } from '@npco/zeller-design-system'
import { useFeatureFlags } from 'features/FeatureFlags'
import { ScrollableContentWithNewBadge } from 'features/StartupModals/common/components/ScrollableContentWithNewBadge/ScrollableContentWithNewBadge'

import { useIsMobileResolution } from 'hooks/useIsMobileResolution'
import { BREAKPOINT } from 'styles/breakpoints'

import { Subtitle } from '../../../SetupFlow/SetupFlowModal/SetupFlowModal.styled'
import { SavingsAccountContentEntitySavingsAccountProductFragment } from './SavingsAccountContent.generated'
import { savingsAccountContentTranslations } from './SavingsAccountContent.i18n'
import * as styled from './SavingsAccountContent.styled'

type SavingsAccountContentProps = {
  entitySavingsAccountProduct: SavingsAccountContentEntitySavingsAccountProductFragment
}

export const SavingsAccountContent = ({
  entitySavingsAccountProduct,
}: SavingsAccountContentProps) => {
  const t = useTranslations(savingsAccountContentTranslations)

  const hasBonus =
    entitySavingsAccountProduct.bonusInterestRate &&
    Number(entitySavingsAccountProduct.bonusInterestRate) !== 0

  const isMobile = useIsMobileResolution()
  const isTablet = useIsMobileResolution(BREAKPOINT.SM)
  const isDesktopORMobile = (isMobile && isTablet) || (!isMobile && !isTablet)

  const flags = useFeatureFlags()

  return (
    <ScrollableContentWithNewBadge>
      <styled.Title>
        {t('title', {
          lineBreak: isDesktopORMobile && <br />,
        })}
      </styled.Title>
      <Subtitle>{t('description')}</Subtitle>

      <Flex flexDirection="column" gap="4px" lineHeight="24px">
        <Tutorial.BulletPoint
          iconColour={COLOR.GREEN_1000}
          text={t('point1', {
            effectiveInterestRate:
              entitySavingsAccountProduct.effectiveInterestRate,
          })}
        />
        <Tutorial.BulletPoint
          iconColour={COLOR.GREEN_1000}
          text={t('point2')}
        />
        <Tutorial.BulletPoint
          iconColour={COLOR.GREEN_1000}
          text={t('point3')}
        />
        <Tutorial.BulletPoint
          iconColour={COLOR.GREEN_1000}
          text={t('point4')}
        />
      </Flex>
      {(hasBonus || flags.SavingsAccountMarch) && (
        <Box
          backgroundColor="#EAFFB5"
          p="12px 16px 16px 16px"
          borderRadius="8px"
        >
          <styled.InterestHeading>
            {flags.SavingsAccountMarch
              ? t('interestHeadingMarch')
              : t('interestHeading')}
          </styled.InterestHeading>
          <styled.InterestText>
            {flags.SavingsAccountMarch
              ? t('interestDescriptionMarch', {
                  interestRate: (
                    <styled.InterestEmphasis>
                      {t('baseEmphasis', {
                        baseInterestRate:
                          entitySavingsAccountProduct.effectiveInterestRate,
                      })}
                    </styled.InterestEmphasis>
                  ),
                })
              : t('interestDescription', {
                  bonusEmphasis: (
                    <styled.InterestEmphasis>
                      {t('bonusEmphasis', {
                        bonusInterestRate:
                          entitySavingsAccountProduct.bonusInterestRate,
                      })}
                    </styled.InterestEmphasis>
                  ),
                  baseEmphasis: (
                    <styled.InterestEmphasis>
                      {t('baseEmphasis', {
                        baseInterestRate:
                          entitySavingsAccountProduct.baseInterestRate,
                      })}
                    </styled.InterestEmphasis>
                  ),
                  bonusLengthInDays:
                    entitySavingsAccountProduct.bonusLengthInDays,
                })}
          </styled.InterestText>
        </Box>
      )}
      <styled.TermsAndConditionsText>
        *{' '}
        <styled.TermsAndConditionsLink href={TERMS_CONDITIONS_URL}>
          {t('termsApply')}
        </styled.TermsAndConditionsLink>
      </styled.TermsAndConditionsText>
    </ScrollableContentWithNewBadge>
  )
}

SavingsAccountContent.fragments = {
  EntitySavingsAccountProduct: gql`
    fragment SavingsAccountContentEntitySavingsAccountProductFragment on EntitySavingsAccountProduct {
      baseInterestRate
      bonusInterestRate
      bonusLengthInDays
      effectiveInterestRate
    }
  `,
}
